import React, {KeyboardEvent} from 'react';

import {IFormData} from '../../components/CodeValidationForm/schema';
import {UseVerificationProps, HandleInputParamsType} from './types';

export const VALIDATION_CODE_NAME: Record<number, keyof IFormData> = {
	1: 'code1',
	2: 'code2',
	3: 'code3',
	4: 'code4',
	5: 'code5',
	6: 'code6',
	7: 'code7',
};

const BACKSPACE_KEY = 'Backspace';
const ARROW_LEFT_KEY = 'ArrowLeft';
const ARROW_UP_KEY = 'ArrowUp';
const ARROW_DOWN_KEY = 'ArrowDown';
const ARROW_RIGHT_KEY = 'ArrowRight';
const DEAD_KEY = 'Dead';
const DEAD_KEY_HOLDER = '_';

export function useVerification({codeLength, methods}: UseVerificationProps) {
	const {setValue, getValues} = methods;

	const inputClass = 'code-digit';
	const lastIndex = codeLength - 1;

	const handleInputFocusOrBlur = ({index, blur}: HandleInputParamsType) => {
		const inputElement = document.querySelectorAll(`.${inputClass} > div > input`)[
			index
		] as HTMLInputElement;

		if (blur) {
			inputElement.blur();
			return undefined;
		}

		if (index >= 0) {
			inputElement.focus();
		}
		return undefined;
	};

	const handlePaste = (
		e: React.ClipboardEvent<HTMLInputElement | HTMLDivElement>,
		index: number,
	) => {
		e.preventDefault();

		const pastedData = e.clipboardData.getData('text');
		const formatDataPastedNumber = pastedData.replace(/[^\d]/g, '');
		const dataPastedLength = formatDataPastedNumber.slice(0, codeLength);

		Array.from(dataPastedLength).forEach((value, keyCode) => {
			setValue(VALIDATION_CODE_NAME[keyCode + 1], value, {shouldValidate: true});
		});

		if (dataPastedLength.length < codeLength) {
			for (let i = dataPastedLength.length; i < codeLength; i += 1) {
				setValue(VALIDATION_CODE_NAME[i + 1], '', {shouldValidate: true});
			}
		}

		const nextInputIndex = dataPastedLength.length;
		if (nextInputIndex < codeLength) {
			handleInputFocusOrBlur({index: nextInputIndex});
		} else {
			handleInputFocusOrBlur({index, blur: true});
		}
	};

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		index: number,
	) => {
		const entry = e.target.value;

		const currentInput = VALIDATION_CODE_NAME[index + 1];
		const nextIndex = index + 1;

		if (entry.length <= 1 && !Number.isNaN(entry)) {
			setValue(currentInput, entry, {shouldValidate: true});
		} else {
			setValue(currentInput, entry.slice(1), {shouldValidate: true});
		}

		if (entry.length > 0) {
			if (index < lastIndex) {
				handleInputFocusOrBlur({index: nextIndex});
				return;
			}
			handleInputFocusOrBlur({index: lastIndex, blur: true});
		}
	};

	const handleKeyDown = (
		e: KeyboardEvent<HTMLDivElement | HTMLInputElement | HTMLTextAreaElement>,
		index: number,
	) => {
		const {key} = e;
		const currentInput = VALIDATION_CODE_NAME[index + 1];
		const currentValue = getValues(currentInput);
		const nextIndex = index + 1;
		const prevIndex = index - 1;

		if (key === ARROW_RIGHT_KEY || key === ARROW_DOWN_KEY) {
			e.preventDefault();
			if (index < lastIndex) {
				return handleInputFocusOrBlur({index: nextIndex});
			}
		}

		if (key === ARROW_LEFT_KEY || key === ARROW_UP_KEY) {
			e.preventDefault();
			if (index > 0) {
				return handleInputFocusOrBlur({index: prevIndex});
			}
		}

		if (key === BACKSPACE_KEY) {
			e.preventDefault();
			if (currentValue.length === 0 && index > 0) {
				setValue(VALIDATION_CODE_NAME[prevIndex + 1], '', {shouldValidate: true});
				return handleInputFocusOrBlur({index: prevIndex});
			}
			if (currentValue.length > 0) {
				setValue(currentInput, '', {shouldValidate: true});
				return handleInputFocusOrBlur({index: prevIndex});
			}
		}

		if (key === DEAD_KEY) {
			e.preventDefault();
			if (currentValue.length > 0) {
				setValue(currentInput, currentValue, {shouldValidate: true});
				return undefined;
			}
			setValue(currentInput, DEAD_KEY_HOLDER, {shouldValidate: true});
		}

		const invalidKeys = ['e', 'E', '+', '-', '.'];

		if (invalidKeys.includes(e.key)) {
			e.preventDefault();
		}
		return undefined;
	};

	return {inputClass, handleChange, handleKeyDown, handlePaste};
}
